// ★ EDITING ONE LAYER OF THE IMAGE PROFILE — the control both surfaces use.
//
// Omar, 2026-09-07, decision 8: "Please add editing and saving to the current
// Console implementation, covering both the shared photography instructions and
// each client's illustration style. Keep mandatory layout requirements, such as
// image dimensions, protected from these edits."
//
// ONE component, used by the shared photography screen and by a customer's
// illustration style, because they are the same act on two scopes. A second
// implementation would drift, and the half that drifted would be the one nobody
// was looking at (`feedback_one_rule_one_place`).
//
// ── Four things this control does that a plain textarea does not ────────────
//
//  1. IT SAYS WHERE THE TEXT CAME FROM. "Factory" and "Edited" are different
//     states and a box full of text looks identical in both. Without the badge,
//     an operator cannot tell whether the words on screen are a default nobody
//     chose or a decision a colleague made
//     (`feedback_an_empty_control_hides_the_default_it_avoids`).
//  2. RESTORE IS ONLY OFFERED WHEN THERE IS SOMETHING TO RESTORE. On factory
//     text the button would do nothing, and a control that does nothing when
//     pressed is a false affordance (`feedback_no_false_affordance_toggles`).
//  3. SAVE IS DISABLED UNTIL THE TEXT ACTUALLY DIFFERS, so "Save" pressed twice
//     cannot quietly write an identical row and move the timestamp.
//  4. IT READS BACK WHAT THE SERVER STORED rather than keeping its own copy. The
//     response replaces the editor's text, so a save that landed differently
//     from what was typed is visible immediately
//     (`feedback_a_failure_state_must_read_back_not_infer`).

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import type {
  ApiError,
  ConsoleApi,
  CustomerIllustrationStyle,
  ImageProfileSource,
} from "./api.js";
import { surfaceLabel } from "./chrome.js";
import {
  ErrorState,
  InlineError,
  Loading,
  Section,
  fmtDateTime,
  useAsync,
  useSubmitLatch,
} from "./ui.js";

/** Must match `PROFILE_TEXT_MAX` in the gateway's admin routes. */
const PROFILE_TEXT_MAX = 20_000;

const TEXTAREA_STYLE: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 220,
  padding: "12px 14px",
  background: "var(--surface-inset)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  fontFamily: "var(--font-mono)",
  fontSize: 11.5,
  lineHeight: 1.6,
  color: "var(--text)",
  // ★ THE BOX GROWS TO ITS TEXT — Omar, 2026-09-15: "Remove the vertical scroll
  //   for the prompts on all the tabs (i.e. Visual craft, etc)". `overflowY`
  //   must be hidden for `scrollHeight` to settle: with it `auto`, the
  //   measurement includes room the scrollbar itself takes and the box creeps a
  //   few pixels on every keystroke.
  overflowY: "hidden",
  // `none`, not `vertical`: the element re-fits itself on every change, so a
  // drag handle would spring back on the next keypress — an affordance that
  // visibly does nothing is worse than none (`feedback_no_false_affordance_toggles`).
  resize: "none",
};

/**
 * Size a textarea to its own content, so a long instruction is read by scrolling
 * the PAGE rather than a box inside it.
 *
 * ⚠️ `height` must be cleared to `auto` FIRST. `scrollHeight` never reports less
 * than the element's current height, so measuring without the reset makes the
 * box a one-way ratchet: it grows when text is added and never shrinks when text
 * is deleted.
 */
function useFitToContent(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null);
  // Layout effect, not effect: this runs before paint, so the box is never shown
  // at the wrong height for a frame.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // ★ THE BORDER HAS TO BE ADDED BACK. `scrollHeight` is content + padding and
    //   excludes the border, but this box is `box-sizing: border-box`, so the
    //   `height` we set INCLUDES it. Assigning `scrollHeight` alone leaves the
    //   content box 2px short — measured in a real browser as clientHeight 629
    //   against scrollHeight 631, i.e. a two-pixel scrollbar-less internal
    //   scroll that no test and no screenshot would ever show.
    const cs = getComputedStyle(el);
    const border =
      cs.boxSizing === "border-box"
        ? parseFloat(cs.borderTopWidth || "0") + parseFloat(cs.borderBottomWidth || "0")
        : 0;
    el.style.height = `${el.scrollHeight + border}px`;
  }, [value]);
  return ref;
}

export interface ProfileLayerEditorProps {
  title: string;
  /** Why this layer exists, in the operator's terms. */
  description: string;
  /** What a generation would use right now. */
  value: string;
  source: ImageProfileSource;
  /** The protected original. Restoring sends `null`; this is only for display. */
  factory: string;
  /** How long the text may be — the server refuses past this too. */
  maxLength: number;
  /** `null` means "restore the factory text". Resolves to the STORED value. */
  onSave: (next: string | null) => Promise<{ value: string; source: ImageProfileSource }>;
  /** Told when a save lands, so the page can refresh anything derived from it. */
  onSaved?: (next: { value: string; source: ImageProfileSource }) => void;
}

export function ProfileLayerEditor({
  title,
  description,
  value,
  source,
  factory,
  maxLength,
  onSave,
  onSaved,
}: ProfileLayerEditorProps) {
  const [draft, setDraft] = useState(value);
  const [current, setCurrent] = useState({ value, source });
  const [err, setErr] = useState<ApiError | null>(null);
  const [justSaved, setJustSaved] = useState<string | null>(null);
  const { run, busy } = useSubmitLatch();

  // ⚠️ Re-sync when the page reloads the profile underneath us. Without this the
  // editor keeps showing a stale copy of props, which is the exact defect
  // `feedback_a_local_mirror_of_props_goes_stale` is about.
  useEffect(() => {
    setDraft(value);
    setCurrent({ value, source });
  }, [value, source]);

  const fit = useFitToContent(draft);

  const dirty = draft !== current.value;
  const isFactory = current.source === "factory";
  const tooLong = draft.length > maxLength;

  const apply = (next: string | null) => {
    setErr(null);
    setJustSaved(null);
    run(
      () => onSave(next),
      (stored) => {
        setCurrent(stored);
        setDraft(stored.value);
        setJustSaved(
          stored.source === "factory"
            ? "Restored to the protected original."
            : "Saved. New images will use this; images already generated are unchanged.",
        );
        onSaved?.(stored);
      },
      (e) => setErr(e),
    );
  };

  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--text-strong)" }}>
          {title}
        </h3>
        {/* ★ Not a decoration. "Factory" and "Edited" are the two states an
            operator cannot otherwise tell apart. */}
        {/* `proposed` is the accent pill — this is a state, not a success and
            not a warning. `pending` is the muted one, which is right for text
            nobody has chosen. */}
        <span className={isFactory ? "pill pending" : "pill proposed"}>
          {isFactory ? "Original" : "Edited"}
        </span>
      </div>

      <p style={{ margin: "0 0 10px", fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
        {description}
      </p>

      <textarea
        ref={fit}
        style={TEXTAREA_STYLE}
        value={draft}
        spellCheck={false}
        disabled={busy}
        aria-label={title}
        onChange={(e) => {
          setDraft(e.target.value);
          setJustSaved(null);
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginTop: 8,
        }}
      >
        <button
          type="button"
          className="btn primary"
          disabled={busy || !dirty || tooLong}
          onClick={() => apply(draft)}
        >
          {busy ? "Saving…" : "Save"}
        </button>

        {/* Offered ONLY when something has been saved over the original. On
            factory text this button would do nothing at all. */}
        {!isFactory && (
          <button type="button" className="btn" disabled={busy} onClick={() => apply(null)}>
            Restore the original
          </button>
        )}

        {dirty && !busy && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setDraft(current.value);
              setJustSaved(null);
            }}
          >
            Discard changes
          </button>
        )}

        <span
          style={{
            fontSize: 11.5,
            color: tooLong ? "var(--error-text)" : "var(--text-muted)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {draft.length.toLocaleString()} / {maxLength.toLocaleString()} characters
        </span>
      </div>

      {tooLong && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--error-text)" }}>
          This is longer than the limit, so Save is unavailable. Shorten it and try again — nothing
          has been saved.
        </p>
      )}

      {justSaved && !err && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-muted)" }}>{justSaved}</p>
      )}

      {err && (
        <div style={{ marginTop: 8 }}>
          <InlineError err={err} />
        </div>
      )}

      {/* The protected original, visible without leaving the page — so an
          operator can compare before deciding to restore. Collapsed by default
          because on this screen it is reference, not the subject. */}
      {!isFactory && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ fontSize: 12, color: "var(--text-muted)", cursor: "pointer" }}>
            Show the protected original
          </summary>
          <pre
            style={{
              margin: "8px 0 0",
              padding: "12px 14px",
              background: "var(--surface-inset)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              fontFamily: "var(--font-mono)",
              fontSize: 11.5,
              lineHeight: 1.6,
              color: "var(--text-muted)",
              whiteSpace: "pre-wrap",
              // No vertical cap here either — it is the same prompt text, shown
              // for comparison, and the same instruction applies to it.
              overflowX: "auto",
            }}
          >
            {factory}
          </pre>
        </details>
      )}
    </div>
  );
}

/**
 * ★ ONE CUSTOMER'S ILLUSTRATION STYLE — the "dedicated area in each company's
 *   Console detail page" Omar asked for on 2026-09-07.
 *
 * It sits on the company page and nowhere else, deliberately. The shared
 * Image-profiles screen shows the protected original for reference and does not
 * offer to edit it, because a setting with two editors has two answers
 * (`feedback_one_rule_one_place`).
 *
 * ⚠️ THE SENTENCE ABOUT SCOPE IS PART OF THE FEATURE, not decoration. An editor
 * for "the illustration style" on a page about one company is ambiguous —
 * without saying so, a reasonable operator could believe they were about to
 * change what every customer gets. The panel says whose it is, and what a
 * photograph does instead.
 */
export function IllustrationStyleSection({
  api,
  id,
  companyName,
}: {
  api: ConsoleApi;
  id: string;
  companyName: string;
}) {
  const { state, retry } = useAsync<CustomerIllustrationStyle>(
    () => api.illustrationStyle(id),
    [id],
  );

  return (
    <Section title="Illustration style">
      <p style={{ margin: "0 0 12px", fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
        How AI-generated <strong>illustrations</strong> are drawn for {companyName}. This applies to
        this customer only — changing it does not affect any other company.{" "}
        {/* ★ The screen's name comes from the rail, never retyped. It was
            retyped once and went stale the day Omar renamed the screen — see
            `surfaceLabel`. */}
        <strong>Photographs are not affected</strong>: they use the shared instructions on the{" "}
        {surfaceLabel("imageProfiles")} screen. The required image sizes are set by the layout and
        cannot be changed here.
      </p>

      {state.t === "loading" && <Loading label="this customer's illustration style" />}
      {state.t === "err" && <ErrorState err={state.err} onRetry={retry} />}
      {state.t === "ok" && (
        <>
          <ProfileLayerEditor
            title={`Illustration style for ${companyName}`}
            description="Only used when an author chooses Illustration rather than Photograph. This is the one kind of picture the company's brand colours ever reach."
            value={state.data.illustration}
            source={state.data.source}
            factory={state.data.factory}
            maxLength={PROFILE_TEXT_MAX}
            onSave={async (next) => {
              const r = await api.saveIllustrationStyle(id, next);
              return { value: r.illustration, source: r.source };
            }}
            onSaved={retry}
          />
          <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
            Last changed: {fmtDateTime(state.data.updatedAt)}
            {state.data.updatedBySub ? " · by a member of Dynamo staff" : ""}.
          </p>
        </>
      )}
    </Section>
  );
}
